import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Store, LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import WaveAnimation from './Animations/WaveAnimation';
import { config } from '../config/env';
import { useAppStore } from '../stores/useAppStore';

const Login = () => {
  const appIcon = config.appIcon;
  const { businessInfo } = useAppStore();
  const [tabValue, setTabValue] = useState(0);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [backgroundAnimation, setBackgroundAnimation] = useState(0);

  const { login } = useAuth(); // register temporarily disabled
  const navigate = useNavigate();

  // Background configurations
  const backgrounds = [
    {
      gradient: 'from-indigo-500 via-purple-500 to-pink-500',
      particles: '#ffffff',
      title: 'Welcome Back',
      subtitle: 'Sign in to your account'
    },
    {
      gradient: 'from-blue-400 via-cyan-400 to-blue-500',
      particles: '#ffffff',
      title: 'Professional',
      subtitle: 'Business-grade POS system'
    },
    {
      gradient: 'from-green-400 via-emerald-400 to-teal-500',
      particles: '#ffffff',
      title: 'Innovation',
      subtitle: 'Modern retail management'
    }
  ];

  // Animated background effect
  useEffect(() => {
    const interval = setInterval(() => {
      setBackgroundAnimation(prev => (prev + 1) % backgrounds.length);
    }, 8000); // Change background every 8 seconds

    return () => clearInterval(interval);
  }, []);

  // Change background when tab changes
  useEffect(() => {
    setBackgroundAnimation(tabValue);
  }, [tabValue]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Trim the username: phone keyboards and autofill often append a space,
    // and "manager " doesn't match "manager". Usernames never contain spaces.
    // The password is sent as typed.
    const result = await login(loginData.username.trim(), loginData.password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  // TEMPORARILY DISABLED - Register handler
  // const handleRegister = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);
  //   setError('');

  //   const result = await register(registerData);
    
  //   if (result.success) {
  //     setError('');
  //     // Automatically log in the user after successful registration
  //     const loginResult = await login(registerData.username, registerData.password);
  //     if (loginResult.success) {
  //       navigate('/');
  //     } else {
  //       setError(loginResult.error);
  //       setTabValue(0);
  //       setLoginData({ username: registerData.username, password: '' });
  //     }
  //   } else {
  //     setError(result.error);
  //   }
    
  //   setLoading(false);
  // };


  const BACKGROUND_COUNT = backgrounds.length;
  const safeIndex = BACKGROUND_COUNT > 0 ? (backgroundAnimation % BACKGROUND_COUNT + BACKGROUND_COUNT) % BACKGROUND_COUNT : 0;
  const currentBg = backgrounds[safeIndex] || { gradient: 'from-indigo-500 via-purple-500 to-pink-500', particles: '#ffffff', title: 'Welcome Back', subtitle: 'Sign in to your account' };

  return (
    <div className={`min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br ${currentBg.gradient} transition-all duration-1000`}>
      {/* Animated Background Particles */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-radial from-white via-transparent to-transparent animate-pulse"></div>
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-white to-transparent animate-pulse delay-1000"></div>
      </div>

      {/* Wave Animations */}
      <WaveAnimation 
        color={currentBg.particles} 
        opacity={0.15} 
        speed={25} 
        height={120} 
        position="bottom" 
        waveCount={3} 
      />
      <WaveAnimation 
        color={currentBg.particles} 
        opacity={0.08} 
        speed={30} 
        height={80} 
        position="bottom" 
        waveCount={2} 
      />
      <WaveAnimation 
        color={currentBg.particles} 
        opacity={0.12} 
        speed={20} 
        height={100} 
        position="top" 
        waveCount={2} 
      />

      {/* Main Content */}
      <div className="w-full max-w-md mx-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 animate-fade-in">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src={appIcon} alt="App Icon" className="h-20 w-20 rounded-full object-contain" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              {businessInfo?.BusinessName || config.appTitle}
            </h1>
            <p className="text-gray-600 font-medium">
              {currentBg.subtitle}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex mb-6 bg-gray-100 rounded-xl p-1">
            {/* <button
              onClick={() => setTabValue(0)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                tabValue === 0
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <LogIn className="w-4 h-4" />
              Sign In
            </button> */}
            {/* TEMPORARILY DISABLED - Signup functionality */}
            {/* <button
              onClick={() => setTabValue(1)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                tabValue === 1
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              Sign Up
            </button> */}
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-600 font-medium">{error}</p>
            </div>
          )}

          {/* Forms */}
          <div className="transition-all duration-500">
            {tabValue === 0 ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    {/* Shown as plain text the phone must not "fix" the password,
                        hence no autocapitalize/autocorrect/spellcheck. */}
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 [&::-ms-reveal]:hidden"
                      placeholder="Enter your password"
                    />
                    {/* Out of the tab order so username → password → Enter stays
                        the keyboard flow; Enter must submit, not toggle. */}
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-500 hover:text-gray-700"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Signing In...
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">Signup functionality is temporarily disabled.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
